import {
    DefectSignature,
    DefectStatus,
} from '../types';
import {
    classifyDefect,
    DefectKind,
} from './classifyDefect';

// FNP-904 — the triage summary strip above the table.
//
// The single number an operator actually needs is LIVE: how many defects are
// happening now. Total signature count grows monotonically with history and is
// a weak health signal on its own — the same reasoning that made the FNP-903
// Done-when box "≤ 20 signatures, of which ≤ 3 live".

// A defect is "live" if it has been seen within this window. Matches the
// engine's RESOLUTION_WINDOW_HOURS so the console's idea of live and the
// sweep's idea of resolved cannot contradict each other.
export const LIVE_WINDOW_HOURS = 6;

export type DefectSummary = {
    total: number,
    live: number,
    unverifiedEffect: number,
    regressions: number,
    unfiled: number,
    resolved: number,
};

export const isLive = (record: Pick<DefectSignature, 'lastSeenAt'>, now: Date = new Date()): boolean => {
    if (!record.lastSeenAt) {
        return false;
    }
    const cutoff = new Date(now.getTime() - LIVE_WINDOW_HOURS * 60 * 60 * 1000).toISOString();
    return record.lastSeenAt >= cutoff;
};

export const summarizeDefects = (
    records: DefectSignature[],
    now: Date = new Date(),
): DefectSummary => {
    return records.reduce<DefectSummary>((acc, record) => {
        acc.total += 1;

        if (isLive(record, now)) {
            acc.live += 1;
        }

        const kind = classifyDefect(record);
        if (kind === DefectKind.UNVERIFIED_EFFECT) {
            acc.unverifiedEffect += 1;
        }
        if (kind === DefectKind.REGRESSION) {
            acc.regressions += 1;
        }
        if (record.status === DefectStatus.RESOLVED) {
            acc.resolved += 1;
        }
        // "Unfiled" counts only defects a human might still act on — a resolved
        // one needs no ticket, so including it would inflate the number that is
        // supposed to mean "outstanding work".
        if (!record.jiraKey && record.status !== DefectStatus.RESOLVED) {
            acc.unfiled += 1;
        }

        return acc;
    }, {
        total: 0,
        live: 0,
        unverifiedEffect: 0,
        regressions: 0,
        unfiled: 0,
        resolved: 0,
    });
};
